import React, { useState, useEffect } from "react";
import { BookingRecord } from "../types";
import { updateBookingStatus } from "../services/storageService";
import {
  QrCode,
  CreditCard,
  Building2,
  Store,
  ChevronRight,
  Copy,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Lock,
  ArrowLeft,
} from "lucide-react";
import Swal from "sweetalert2";

interface PaymentGatewayProps {
  booking: BookingRecord;
  onSuccess: () => void;
  onBack?: () => void;
}

type PaymentChannel = "QRIS" | "VA" | "RETAIL";
type BankCode = "BCA" | "MANDIRI" | "BNI" | "BRI" | "CIMB" | "PERMATA";

export const PaymentGateway: React.FC<PaymentGatewayProps> = ({
  booking,
  onSuccess,
  onBack,
}) => {
  const [selectedChannel, setSelectedChannel] = useState<PaymentChannel | null>(
    null
  );
  const [selectedBank, setSelectedBank] = useState<BankCode | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [timeLeft, setTimeLeft] = useState(3600); // 1 Hour timer
  const [vaNumber, setVaNumber] = useState("");

  // Timer Countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, "0")}:${m
      .toString()
      .padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  const generateVA = (bank: BankCode) => {
    // Mock VA Number generation based on bank and booking code
    const prefixes: Record<BankCode, string> = {
      BCA: "7810",
      MANDIRI: "8922",
      BNI: "8277",
      BRI: "2299",
      CIMB: "1199",
      PERMATA: "8855",
    };
    const random = Math.floor(10000000 + Math.random() * 90000000);
    setVaNumber(`${prefixes[bank]}${random}`);
  };

  const handleSelectBank = (bank: BankCode) => {
    setSelectedBank(bank);
    generateVA(bank);
  };

  const simulatePaymentCheck = () => {
    setIsProcessing(true);
    // Simulate API Call delay
    setTimeout(() => {
      updateBookingStatus(booking.id, "PAID");
      setIsProcessing(false);
      onSuccess();
    }, 2000);
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    Swal.fire({
      icon: "success",
      title: "Tersalin",
      text: "Tersalin ke clipboard!",
      toast: true,
      position: "top-end",
      timer: 2000,
      showConfirmButton: false,
    });
  };

  return (
    <div className="bg-gray-50 min-h-[500px] flex flex-col animate-fadeIn rounded-b-2xl overflow-hidden">
      {/* Header iPaymu Style */}
      <div className="bg-white border-b border-gray-200 p-4 shadow-sm sticky top-0 z-10">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <div className="text-xl font-bold italic tracking-tighter text-[#2D9CDB]">
              iPaymu
            </div>
            <div className="h-4 w-px bg-gray-300 mx-1"></div>
            <span className="text-xs text-gray-500 font-medium">
              Secure Payment
            </span>
          </div>
          <div className="flex items-center gap-1 text-xs font-mono font-bold text-orange-600 bg-orange-50 px-2 py-1 rounded">
            <RefreshCw className="w-3 h-3 animate-spin" />{" "}
            {formatTime(timeLeft)}
          </div>
        </div>

        <div className="flex justify-between items-end">
          <div>
            <p className="text-xs text-gray-500 mb-1">Total Pembayaran</p>
            <p className="text-2xl font-bold text-gray-900">
              Rp {booking.totalPrice.toLocaleString("id-ID")}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500 mb-1">Order ID</p>
            <p className="text-sm font-bold text-gray-700 font-mono">
              {booking.code}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-4 space-y-4">
        {/* Channel: QRIS */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <button
            onClick={() =>
              setSelectedChannel(selectedChannel === "QRIS" ? null : "QRIS")
            }
            className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center">
                <QrCode className="w-6 h-6" />
              </div>
              <div className="text-left">
                <p className="font-bold text-gray-800 text-sm">QRIS</p>
                <p className="text-[10px] text-gray-500">
                  GoPay, OVO, Dana, ShopeePay, LinkAja
                </p>
              </div>
            </div>
            {selectedChannel === "QRIS" ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {selectedChannel === "QRIS" && (
            <div className="p-6 border-t border-gray-100 bg-gray-50 flex flex-col items-center animate-slideUp">
              <div className="bg-white p-3 rounded-xl shadow-sm border border-gray-200 mb-4">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=GIZPARK-${booking.code}-IPAYMU`}
                  alt="QRIS"
                  className="w-48 h-48 mix-blend-multiply"
                  loading="lazy"
                />
              </div>
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a2/Logo_QRIS.svg/1200px-Logo_QRIS.svg.png"
                alt="QRIS Logo"
                className="h-8 mb-4 opacity-80"
                loading="lazy"
              />
              <p className="text-xs text-center text-gray-500 max-w-xs">
                Scan QR code di atas menggunakan aplikasi E-Wallet atau Mobile
                Banking Anda.
              </p>
            </div>
          )}
        </div>

        {/* Channel: Virtual Account */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <button
            onClick={() =>
              setSelectedChannel(selectedChannel === "VA" ? null : "VA")
            }
            className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-50 text-purple-600 rounded-lg flex items-center justify-center">
                <CreditCard className="w-6 h-6" />
              </div>
              <div className="text-left">
                <p className="font-bold text-gray-800 text-sm">
                  Virtual Account
                </p>
                <p className="text-[10px] text-gray-500">
                  BCA, Mandiri, BNI, BRI, CIMB, Permata
                </p>
              </div>
            </div>
            {selectedChannel === "VA" ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {selectedChannel === "VA" && (
            <div className="p-4 border-t border-gray-100 bg-gray-50 animate-slideUp">
              {!selectedBank ? (
                <div className="grid grid-cols-2 gap-3">
                  {["BCA", "MANDIRI", "BNI", "BRI", "CIMB", "PERMATA"].map(
                    (bank) => (
                      <button
                        key={bank}
                        onClick={() => handleSelectBank(bank as BankCode)}
                        className="bg-white p-3 rounded-lg border border-gray-200 hover:border-blue-500 hover:shadow-md transition-all flex items-center gap-2"
                      >
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center font-bold text-[10px] text-gray-600">
                          {bank.substring(0, 2)}
                        </div>
                        <span className="font-bold text-sm text-gray-700">
                          {bank}
                        </span>
                      </button>
                    )
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <button
                    onClick={() => {
                      setSelectedBank(null);
                      setVaNumber("");
                    }}
                    className="text-xs text-blue-600 flex items-center gap-1 hover:underline mb-2"
                  >
                    <ArrowLeft className="w-3 h-3" /> Ganti Bank
                  </button>
                  <div className="bg-white p-4 rounded-xl border border-blue-100 shadow-sm">
                    <p className="text-xs text-gray-500 mb-1 uppercase font-bold">
                      Nomor Virtual Account {selectedBank}
                    </p>
                    <div className="flex justify-between items-center">
                      <p className="text-xl font-mono font-bold text-gray-900 tracking-wider">
                        {vaNumber}
                      </p>
                      <button
                        onClick={() => handleCopy(vaNumber)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <Copy className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 space-y-2">
                    <p>1. Masukkan kartu ATM dan PIN.</p>
                    <p>
                      2. Pilih menu <strong>Transaksi Lainnya</strong> {">"}{" "}
                      <strong>Transfer</strong> {">"}{" "}
                      <strong>Ke Rek {selectedBank} Virtual Account</strong>.
                    </p>
                    <p>
                      3. Masukkan nomor VA: <strong>{vaNumber}</strong>.
                    </p>
                    <p>
                      4. Masukkan jumlah pembayaran:{" "}
                      <strong>
                        Rp {booking.totalPrice.toLocaleString("id-ID")}
                      </strong>
                      .
                    </p>
                    <p>5. Ikuti instruksi untuk menyelesaikan transaksi.</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Channel: Retail */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <button
            onClick={() =>
              setSelectedChannel(selectedChannel === "RETAIL" ? null : "RETAIL")
            }
            className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-50 text-red-600 rounded-lg flex items-center justify-center">
                <Store className="w-6 h-6" />
              </div>
              <div className="text-left">
                <p className="font-bold text-gray-800 text-sm">Minimarket</p>
                <p className="text-[10px] text-gray-500">Indomaret, Alfamart</p>
              </div>
            </div>
            {selectedChannel === "RETAIL" ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {selectedChannel === "RETAIL" && (
            <div className="p-6 border-t border-gray-100 bg-gray-50 animate-slideUp">
              <div className="bg-white p-4 rounded-xl border border-gray-200 mb-4 text-center">
                <p className="text-xs text-gray-500 mb-2 uppercase font-bold">
                  Kode Pembayaran
                </p>
                <p className="text-2xl font-mono font-bold text-gray-900 tracking-[0.2em] mb-2">
                  8829 1992 9291
                </p>
                <p className="text-[10px] text-orange-600 bg-orange-50 inline-block px-2 py-1 rounded">
                  Tunjukkan kode ini ke kasir
                </p>
              </div>
              <div className="flex gap-4 justify-center opacity-70">
                <div className="text-xs font-bold text-blue-600 border border-blue-200 px-3 py-1 rounded">
                  INDOMARET
                </div>
                <div className="text-xs font-bold text-red-600 border border-red-200 px-3 py-1 rounded">
                  ALFAMART
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer Action */}
      <div className="p-4 bg-white border-t border-gray-200 mt-auto">
        <button
          onClick={simulatePaymentCheck}
          disabled={isProcessing}
          className="w-full py-4 bg-[#2D9CDB] hover:bg-[#278abf] text-white rounded-xl font-bold text-lg shadow-lg shadow-blue-200 flex items-center justify-center gap-2 transition-all active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {isProcessing ? (
            <>
              <RefreshCw className="w-5 h-5 animate-spin" /> Memproses...
            </>
          ) : (
            <>
              <CheckCircle2 className="w-5 h-5" /> Cek Status Pembayaran
            </>
          )}
        </button>
        <div className="text-center mt-3 flex items-center justify-center gap-1 text-[10px] text-gray-400">
          <Lock className="w-3 h-3" /> Enkripsi SSL 256-bit Aktif
        </div>
      </div>
    </div>
  );
};
